import {
  cloneDeep,
  concat,
  debounce,
  defaultsDeep,
  get,
  has,
  isArray,
  isEmpty,
  isEqual,
  max,
  merge,
  mergeWith,
  min,
  omit,
  pick,
  range,
  set,
  uniq,
} from "lodash";
import moment from "moment";
import { PlotData, Layout, PlotType, PlotRelayoutEvent, Legend } from "plotly.js";
import AutoSizedPlot from "./AutoSizedPlot";
import {
  ITimeChart,
  IDisplay,
  IResult,
  ITimeAxis,
  IOutput,
  ILayout,
  IGetValue,
  IGetProperty,
  IProperty,
  IInput,
  IModel,
  IName,
  ITimeFigure,
  IBase,
} from "../types";
import { IOutputProps } from "./types";
import "./style.scss";
import { doHidden, COLORWAY } from "../utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import ArchiveIcon from "./ArchiveIcon";
import { downloadData } from "./download";
import { range as li } from "utils/math";
import { ChartLoadingSkeleton } from "./loading";
import { isTemplateRefreshing } from "../dependencyIndex";
import RefreshingState from "../RefreshingState";

export interface ITimeChartProps extends IOutputProps {
  template: ITimeChart;
}

interface ITimeChartProperty {
  name: IName;
  sync: ITimeAxis;
  type: "reset" | "update";
  relayout: Partial<Layout>;
}

const default_height = 600;

let _datarevision = 1;

const calculateOffset = (height: number, overview: boolean) => {
  if (overview && height <= 200) {
    return 1.5;
  } else if (height <= 200) {
    return 1.2;
  } else if (height <= 400) {
    return 1.1;
  } else if (height <= 600) {
    return 1.08;
  } else {
    return 1.0 + inverse(li(400, 1000, 0.9, 1.0, height));
  }
};

const inverse = (value: number) => 1.0 - value;

const renderLabel = (display: IDisplay) => {
  const label = display.label ? display.label : "";
  const info = display.info ? ` <span class="pb3-text-muted">${display.info}</span>` : "";
  return `${label}${info}`;
};

const showLegend = (template: ITimeChart): Partial<Legend> | undefined => {
  let temp = [] as Array<string>;
  template.figures.forEach((f) => f.data.forEach((d) => temp.push(`${renderLabel(f)}${renderLabel(d)}`)));
  temp = uniq(temp).filter((t) => t);
  if (temp.length > 1) {
    return {
      orientation: "h",
      yanchor: "top",
      y: calculateOffset(template.height || default_height, template.variant === "overview" && !template.label),
    };
  }
};

const getScopedValue = (
  name: IName,
  getValue: IGetValue | undefined,
  fallback: IModel | IResult,
  defaultValue: any
) => {
  if (getValue) {
    const value = getValue(name);
    return value === undefined ? defaultValue : value;
  }
  const value = get(fallback, name);
  return value === undefined ? defaultValue : value;
};

const findStart = (figure: ITimeFigure, model: IModel, result: IResult, getValue?: IGetValue) => {
  const source = figure.startname || "";
  const scopedStart = source ? getScopedValue(source, getValue, result, undefined) : undefined;
  const start = String(scopedStart ?? get(model, source, figure.start) ?? "");
  return start ? moment(start) : moment().startOf("year");
};

const transformData = (
  template: ITimeChart,
  model: IModel,
  result: IResult,
  getValue?: IGetValue
): Array<Partial<PlotData>> => {
  return concat(
    ...template.figures.map((f, i) =>
      f.data.map((d) => {
        const y = getScopedValue(d.name, getValue, result, []) as Array<number>;
        return {
          type: "scattergl" as PlotType,
          mode: "lines" as any,
          line: {
            ...(y?.length <= 1000 && { shape: "spline" as any }),
            size: 3,
            width: 2,
          },
          name: renderLabel(d),
          legendgroup: renderLabel(f),
          x: range(0, y?.length).map((v) =>
            findStart(f, model, result, getValue)
              .add(v * d.interval, "minutes")
              .format()
          ),
          y,
          ...(i > 0 && { yaxis: `y${i + 1}` }),
        } as Partial<PlotData>;
      })
    )
  ).filter((v) => !isEmpty(v.x) && !isEmpty(v.y));
};

const createLayout = (
  template: ITimeChart,
  result: IResult,
  getValue: IGetValue | undefined,
  maxGrouped: number,
  ranges: [[string, string] | undefined, [number, number] | undefined]
): Partial<Layout> => {
  const count = template.figures.length;
  const rangeslider = {
    rangeslider: {
      visible: true,
      range: ranges[0],
    },
  };
  const fixed = { fixedrange: true };
  const axisZoom = (obj: ITimeChart) => [
    ["none"].includes(obj.zoom || "") ? fixed : {},
    ["x_only", "none"].includes(obj.zoom || "") ? fixed : {},
  ];
  const [xzoom, yzoom] = axisZoom(template);
  const [xaxis, yaxis] = template.axis || [];
  const [xrange, yrange] = template.range || [];
  const legend = showLegend(template);
  const grouped = !legend ? 0 : maxGrouped;
  const show = {
    legend: Boolean(legend),
    xaxis: true,
    yaxis: true,
    zoom: true,
  };
  const margin = { r: 0, l: 100, t: 40 + grouped * 19, b: 60 };
  switch (template.variant) {
    case "overview":
      show.zoom = false;
      break;
    case "minimal":
      show.yaxis = false;
      show.xaxis = false;
      show.legend = false;
      margin.t = 20;
      margin.b = 0;
      break;
    case "reduced":
      margin.b = 0;
      break;
    case "default":
    default:
    // keep defaults
  }
  return merge(
    {
      title: { text: template.label, yref: "container", yanchor: "top", y: 1.0, pad: { t: 10 } },
      showlegend: show.legend,
      margin: margin,
      legend: legend,
      xaxis: {
        anchor: "y",
        domain: [0, 1],
        range: ranges[0],
        ...(!show.zoom ? rangeslider : xzoom),
        ...(!show.xaxis && {
          showgrid: false,
          zeroline: false,
          visible: false,
        }),
      },
    },
    count <= 1
      ? {
        yaxis: {
          ...(yaxis && show.yaxis && { title: { text: renderLabel(yaxis) } }),
          range: ranges[1],
          ...(!show.zoom ? fixed : yzoom),
          ...(!show.yaxis && {
            showgrid: false,
            zeroline: false,
            visible: false,
          }),
        },
      }
      : template.figures.reduce(
        (p, _f, i) =>
          merge(p, {
            [`yaxis${i === 0 ? "" : i + 1}`]: {
              ...(yaxis && show.yaxis && { title: { text: renderLabel(yaxis) } }),
              domain: [i * (1.0 / count), (i + 1) * (1.0 / count) - 0.1 / count],
              range: ranges[1],
              ...(!show.zoom ? fixed : yzoom),
            },
          }),
        {}
      ),
    xaxis && show.xaxis && { xaxis: { title: { text: renderLabel(xaxis) } } },
    xrange && { xaxis: { range: getScopedValue(xrange as IName, getValue, result, undefined) } },
    yrange && { yaxis: { range: getScopedValue(yrange as IName, getValue, result, undefined) } }
  );
};

const compute = (values: Array<string>) =>
  Object.values(
    values.reduce((c, v) => {
      if (!(v in c)) {
        c[v] = { frequency: 0, mode: v };
      }
      c[v].frequency++;
      return c;
    }, {} as { [v: string]: { frequency: number; mode: string } })
  ).reduce((a, v) => (v.frequency < a.frequency ? a : v), { frequency: 0, mode: "" });

const findMaxGrouped = (data: Array<Partial<PlotData>>) => {
  const values = data.map((v, i) => v.legendgroup || `${i}`).filter((v) => !isEmpty(v));
  const { frequency } = compute(values);
  return frequency;
};

const findAxisRanges = (
  template: ITimeChart,
  result: IResult,
  getValue: IGetValue | undefined,
  data: Array<Partial<PlotData>>
) => {
  const isValid = (r: (moment.Moment | number | undefined)[] | [moment.Moment, moment.Moment] | [number, number]) =>
    r?.[0] !== undefined && r?.[1] !== undefined && r?.[0] <= r?.[1];
  const [xinit, yinit] = (template.initial?.range || []).map((n) =>
    getScopedValue(n as IName, getValue, result, undefined)
  );
  const initial = [
    isArray(xinit) && isValid(xinit.map((v) => moment(v))) ? xinit.map((v) => moment(v)) : undefined,
    isArray(yinit) && isValid(yinit as [number, number]) ? yinit : undefined,
  ] as [[moment.Moment, moment.Moment] | undefined, [number, number] | undefined];
  const starts = concat(...data.map((d) => d.x?.[0] as string)).map((s) => moment(s));
  const ends = concat(...data.map((d) => d.x?.[d.x?.length - 1] as string)).map((s) => moment(s));
  const xrange = [min(starts), max(ends)];
  const ys = concat(...data.map((d) => d.y as Array<number>));
  const yMin = min(ys) ?? 0;
  const yMax = max(ys) ?? 0;
  const yWidth = yMax - yMin;
  const yrange = [yMin - yWidth * 0.1, yMax + yWidth * 0.1];
  defaultsDeep(initial, [xrange, yrange]);
  return {
    init: [
      initial?.[0] && isValid(initial[0]) ? (initial[0].map((v) => v?.format()) as [string, string]) : undefined,
      initial?.[1] && isValid(initial[1]) ? (initial[1] as [number, number]) : undefined,
    ] as [[string, string] | undefined, [number, number] | undefined],
    home: [
      isValid(xrange) ? (xrange.map((v) => v?.format()) as [string, string]) : undefined,
      isValid(yrange) ? (yrange as [number, number]) : undefined,
    ] as [[string, string] | undefined, [number, number] | undefined],
  };
};

const cleanRelayout = (
  axis: ITimeAxis | undefined,
  relayout?: { xaxis?: any; yaxis?: any }
): { xaxis?: any; yaxis?: any } | undefined => {
  switch (axis) {
    case "none":
      return cloneDeep(omit(relayout, ["xaxis", "yaxis"]));
    case "x_only":
      return cloneDeep(omit(relayout, ["yaxis"]));
    case "x_and_y":
    default:
      return cloneDeep(relayout);
  }
};

const constructRelayout = (
  template: ITimeChart,
  layout: Partial<Layout> | undefined,
  property: ITimeChartProperty | undefined
) => {
  if (!property) {
    return;
  } else if (property.type === "reset") {
    switch (property.sync) {
      case "none":
        return {};
      case "x_only":
        return { xaxis: { range: get(layout, "xaxis.range") } };
      case "x_and_y":
      default:
        return { xaxis: { range: get(layout, "xaxis.range") }, yaxis: { range: get(layout, "yaxis.range") } };
    }
  } else if (property.name === template.name) {
    return property.relayout;
  }
  return cleanRelayout(template.zoom, cleanRelayout(property.sync, property.relayout));
};

const doRelayout = (
  template: ITimeChart,
  layout: Partial<Layout> | undefined,
  getParent: () => IInput | IOutput | ILayout<IBase> | undefined,
  getProperty: IGetProperty | undefined,
  handleProperty: IProperty | undefined,
  ranges: any,
  setRanges: React.Dispatch<any>
) => {
  const parent = getParent();
  const property = parent && parent.name ? (getProperty?.(parent.name) as ITimeChartProperty) : undefined;
  const relayout = constructRelayout(template, layout, property);
  const updateRanges = (obj: any) => {
    const updated = merge(ranges, pick(obj, ["dragmode", "xaxis.range", "yaxis.range"]));
    if (!isEqual(ranges, updated)) {
      setRanges(updated);
    }
  };
  const handleRelayout = (event: Readonly<PlotRelayoutEvent>) => {
    if (parent && ["group", "tab", "explorer", "anchors"].includes(parent.type) && parent.name) {
      const temp = {
        datarevision: _datarevision++,
        ...Object.entries(event).reduce((p, [k, v]) => set(p, k, v), {}),
      } as any;
      const type =
        has(temp, "xaxis.showspikes") || has(temp, "xaxis.autorange") || has(event, "xaxis.autorange")
          ? "reset"
          : "update";
      updateRanges(temp);
      handleProperty?.(parent.name, {
        name: template.name,
        sync: template.sync,
        type: type,
        relayout: temp,
      });
    }
  };
  updateRanges(relayout);
  return { relayout, handleRelayout };
};

const TimeChart = (props: ITimeChartProps) => {
  const { template, model, result, mutators, metadata } = props;
  const { getProperty, handleProperty, getElement, getValue } = mutators;

  const [initial, setInitial] = useState(undefined as Partial<Layout> | undefined);
  const [ranges, setRanges] = useState({} as any);

  const data = useMemo(() => transformData(template, model, result, getValue), [template, model, result, getValue]);
  const maxGrouped = useMemo(() => findMaxGrouped(data), [data]);
  const axisRanges = useMemo(() => findAxisRanges(template, result, getValue, data), [template, result, getValue, data]);

  useEffect(() => {
    if (template.sync === "none") {
      return;
    }
    const next = { xaxis: { range: axisRanges.init[0] }, yaxis: { range: axisRanges.init[1] } };
    setRanges((prev: any) => (isEqual(prev, next) ? prev : next));
  }, [template.sync, axisRanges]);

  const getLayout = useCallback(
    () => createLayout(template, result, getValue, maxGrouped, axisRanges.home),
    [template, result, getValue, maxGrouped, axisRanges]
  );
  const getParent = useCallback(() => getElement?.(template.name)?.parent?.data, [template, getElement]);
  const refreshing = isTemplateRefreshing(template, metadata, mutators.getPath);
  const hidden = doHidden(props);

  if (hidden) {
    return null;
  }

  if (isEmpty(data) && refreshing) {
    return (
      <ChartLoadingSkeleton
        className={template.height ? "chart-container" : "medium-chart-container"}
        height={template.height}
        label={template.label || template.name}
        variant="plot"
      />
    );
  }

  if (isEmpty(data)) {
    return null;
  }

  const layout = getLayout();
  const { relayout, handleRelayout } =
    template.sync !== "none"
      ? doRelayout(template, initial, getParent, getProperty, handleProperty, ranges, setRanges)
      : { relayout: {}, handleRelayout: undefined };
  return (
    <RefreshingState refreshing={refreshing}>
    <div
      className={template.height ? "chart-container" : "medium-chart-container"}
      {...(template.height && {
        style: { height: `${template.height}px` },
      })}
    >
      <AutoSizedPlot
        chartId={template.name}
        data={data}
        layout={mergeWith({ colorway: COLORWAY }, initial || layout, ranges, relayout, (_a, b) =>
          isArray(b) ? b : undefined
        )}
        config={{
          displaylogo: false,
          modeBarButtonsToRemove: ["autoScale2d"],
          modeBarButtonsToAdd: [
            {
              name: "downloadData",
              title: "Download data as a csv",
              icon: ArchiveIcon,
              click: () => downloadData(template, data),
            },
          ],
          scrollZoom: true,
        }}
        onInitialized={(f) =>
          setInitial(
            merge(
              {},
              layout,
              omit(f.layout, ["xaxis.range", "xaxis.autorange", "yaxis.range", "yaxis.autorange"])
            )
          )
        }
        onRelayout={debounce(function (e) {
          handleRelayout?.(e);
        }, 100)}
      />
    </div>
    </RefreshingState>
  );
};

export default TimeChart;
